import type { ConversationPublic } from '@repo/types/chat';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { formatContextualDate } from '@repo/lib/time/formatContextualDate';
import { parseISO } from 'date-fns';
import { UserIcon, UsersIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

export function ConversationsListSkeletonItem(conversationsListSkeletonItemProps: ComponentProps<'li'>) {
  const { className, ...props } = conversationsListSkeletonItemProps;
  return (
    <li className={cn('inline-flex w-full items-center gap-2 rounded-md p-2', className)} {...props}>
      <Skeleton className={'size-10 shrink-0 rounded-md'} />
      <div className={'w-full space-y-2'}>
        <Skeleton className={'h-3 w-full max-w-[20ch]'} />
        <Skeleton className={'h-3 w-full max-w-[14ch]'} />
      </div>
    </li>
  );
}

interface ConversationItemTimeToPresentProps {
  createdAt?: string;
}

function ConversationItemTimeToPresent(conversationItemTimeToPresentProps: ConversationItemTimeToPresentProps) {
  const { createdAt } = conversationItemTimeToPresentProps;
  const [data, setData] = useState<string>('');

  useEffect(() => {
    const formatTimeToPresentDate = (createdAt?: string) => {
      if (!createdAt) return;
      setData(formatContextualDate(parseISO(createdAt)));
    };

    formatTimeToPresentDate(createdAt);

    const interval = setInterval(() => formatTimeToPresentDate(createdAt), 300000);

    return () => clearInterval(interval);
  }, [createdAt]);

  return <p className={'text-text-tertiary shrink-0 text-xs whitespace-nowrap'}>{data}</p>;
}

export function ConversationsListItem(conversationsListItemProps: ConversationPublic) {
  const { id, name, type, message, avatarURL } = conversationsListItemProps;

  const { id: userId } = useCredentials();

  const getObjectKeyByValue = useCallback((obj: Record<string, string>, targetKey: number | undefined) => {
    const entry = Object.entries(obj).find(([key]) => String(key) === String(targetKey));
    return entry ? entry[1] : undefined;
  }, []);

  const chatName = useMemo<string | undefined>(() => {
    if (type === 'individual') {
      try {
        return getObjectKeyByValue(JSON.parse(name), userId);
      } catch (error) {
        console.error('Error parsing chat name:', error);
        return name;
      }
    }
    return name;
  }, [type, name, getObjectKeyByValue, userId]);

  const chatMessage = useMemo<string>(() => {
    if (message?.sender) {
      return `${message.sender.name} ${message.sender.surname.at(0) || ''}: ${message.content}`;
    }
    return 'brak wiadomości';
  }, [message]);

  const isRead = useMemo<boolean>(() => {
    if (!message?.readReceipts) return true;
    return message.sender?.id === userId || message.readReceipts.some((receipt) => receipt.id === userId);
  }, [message?.readReceipts, userId]);

  return (
    <li className={'py-px'}>
      <NavLink
        to={`/chat/${String(id)}`}
        state={{
          id,
          type,
          name: chatName,
          avatarURL,
        }}
        className={({ isActive }) =>
          cn(
            `group transition-outline outline-focus-ring hover:bg-muted/75 active:bg-accent relative inline-flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 opacity-100 outline-0 transition-colors duration-150 ease-out focus-visible:outline-3 disabled:pointer-events-none disabled:opacity-60 aria-disabled:cursor-not-allowed`,
            isActive && 'bg-accent/50'
          )
        }
      >
        <Avatar size={'md'}>
          <AvatarImage src={getAvatar(avatarURL)} alt={'avatar'} />
          <AvatarFallback>{type === 'group' ? <UsersIcon /> : <UserIcon />}</AvatarFallback>
        </Avatar>
        <div className={'flex min-w-0 grow flex-col space-y-0.5 overflow-hidden'}>
          <h5 className={cn('text-accent-foreground w-full truncate text-sm font-medium', !isRead && 'text-foreground font-bold')}>{chatName}</h5>
          <p className={cn('text-muted-foreground truncate text-xs', !isRead && 'text-accent-foreground font-semibold')}>{chatMessage}</p>
        </div>
        <div className={'shrink-0'}>
          <ConversationItemTimeToPresent createdAt={message?.createdAt} />
        </div>
      </NavLink>
    </li>
  );
}
